import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import {
  getProfile,
  login as loginApi,
  logout as logoutApi,
} from "../../api/auth";
import {
  AuthTokens,
  AuthUser,
  LoginOutcome,
  LoginParams,
} from "../../types/auth";

import { AuthRoleSchema } from "@kkp/api-client";
import { z } from "zod";

export type AuthContextValue = {
  isAuthenticated: boolean;
  user: AuthUser | null;
  tokens: AuthTokens | null;
  login: (params: LoginParams) => Promise<LoginOutcome>;
  logout: () => void;
  /**
   * Install a session from a user record already authenticated server-side
   * (e.g. after invite completion or password reset). Cookies must already
   * be set by the server.
   */
  applySession: (user: AuthUser) => void;
};

const SESSION_KEY = "kkp_client_session_v1";

const AuthUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string().optional().default(""),
  role: AuthRoleSchema,
});

const StoredSessionSchema = z.object({
  user: AuthUserSchema.nullable().optional(),
  issuedAt: z.string().optional(),
});

type StoredSession = {
  user?: AuthUser | null;
  tokens: AuthTokens;
  issuedAt?: string;
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

function safeGetLocalStorageItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocalStorageItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore storage errors (private mode, blocked, etc.)
  }
}

function safeRemoveLocalStorageItem(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore storage errors
  }
}

function parseStoredSession(raw: string | null): StoredSession | null {
  if (!raw) return null;
  try {
    const parsed = StoredSessionSchema.parse(JSON.parse(raw));
    const user = parsed?.user ?? null;
    if (!user) return null;
    const issuedAt = parsed?.issuedAt;
    return {
      user,
      tokens: {
        access: "cookie",
        refresh: "cookie",
      },
      issuedAt,
    };
  } catch {
    return null;
  }
}

function persistSession(session: StoredSession): void {
  safeSetLocalStorageItem(
    SESSION_KEY,
    JSON.stringify({
      user: session.user ?? null,
      issuedAt: session.issuedAt,
    }),
  );
}

function clearSession(): void {
  safeRemoveLocalStorageItem(SESSION_KEY);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<StoredSession | null>(() => {
    const raw = safeGetLocalStorageItem(SESSION_KEY);
    const parsed = parseStoredSession(raw);
    if (!parsed && raw) {
      clearSession();
    }
    return parsed;
  });

  const isAuthenticated = Boolean(session?.user);
  const tokens = session?.tokens ?? null;
  const user = session?.user ? session.user : null;

  const login = React.useCallback<AuthContextValue["login"]>(async (params) => {
    const email = params.email.trim();
    const p = params.password;

    const outcome = await loginApi({ email, password: p });
    if (outcome.status === "browserConfirmationRequired") {
      return outcome;
    }

    const { user } = outcome;
    if (!user?.id || !user?.displayName || !user?.email) {
      throw new Error("Missing user profile");
    }

    const nextSession: StoredSession = {
      user: {
        id: user.id,
        displayName: user.displayName,
        email: user.email,
        role: user.role,
      },
      tokens: { access: "cookie", refresh: "cookie" },
      issuedAt: new Date().toISOString(),
    };

    setSession(nextSession);
    persistSession(nextSession);
    return outcome;
  }, []);

  const applySession = React.useCallback<AuthContextValue["applySession"]>(
    (nextUser) => {
      const nextSession: StoredSession = {
        user: {
          id: nextUser.id,
          displayName: nextUser.displayName,
          email: nextUser.email,
          role: nextUser.role,
        },
        tokens: { access: "cookie", refresh: "cookie" },
        issuedAt: new Date().toISOString(),
      };
      setSession(nextSession);
      persistSession(nextSession);
    },
    [],
  );

  const logout = React.useCallback(() => {
    const currentTokens = session?.tokens;
    if (currentTokens) {
      // Best-effort server-side token revocation: fire-and-forget so a failed or
      // slow network call never blocks clearing the local session below.
      void logoutApi(currentTokens).catch(() => {});
    }
    setSession(null);
    clearSession();
  }, [session?.tokens]);

  React.useEffect(() => {
    let mounted = true;
    if (!isAuthenticated || !tokens) return;

    getProfile()
      .then((profile) => {
        if (!mounted) return;
        setSession((prev) => {
          if (!prev) return prev;
          const next: StoredSession = {
            ...prev,
            user: {
              id: profile.id,
              displayName: profile.displayName,
              email: profile.email,
              role: profile.role,
            },
          };
          persistSession(next);
          return next;
        });
      })
      .catch((err) => {
        // ignore profile failures in demo mode
        if (!mounted) return;
        console.warn("Profile fetch failed", err);
      });

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, tokens]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      user,
      tokens,
      login,
      logout,
      applySession,
    }),
    [isAuthenticated, user, tokens, login, logout, applySession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- Auth modules intentionally colocate the context hook with the provider.
export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider />");
  }
  return ctx;
}

/**
 * Route guard for protected routes.
 *
 * Usage:
 * <Route element={<RequireAuth />}>
 *   <Route element={<DashboardLayout />}>
 *     ...
 *   </Route>
 * </Route>
 */
export function RequireAuth() {
  const auth = useAuth();
  const location = useLocation();

  if (!auth.isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return <Outlet />;
}
